import {TourCfg} from './Tour'
import {z} from 'zod'
import {zx} from 'shared/type'
import {doSafe} from 'shared/promise'
import {questionForecastMd} from 'shared/forecast/question'
import {values} from 'remeda'

export const tourCfg = {
  'workspace-select': {
    title: 'Workspace Selector',
    text: `Use the workspace selector to switch between them. Each workspace has its own users and data, and is used for different workshops and organizations.
- Use the same login details to access different workspaces.
- Profile settings (e.g., your picture and name) are unique to each workspace.
`,
    more: 'Workspaces-27f0cba1ade581878a40cb7ee6c0bcac#27f0cba1ade5815895d4fc209545e7a2'
  },
  'notification-menu': {
    title: 'Notifications',
    text: `This is an indicator for how many notifications are unread - click it to read your notifications. Notifications are sent when something happens that you should know about (e.g., someone replies to your comment).

- If you are using the app, notifications will pop up in the bottom right; otherwise, you will be sent an email about it.
- Turn off email notifications via \`Portal Menu\` (top right) -> \`Edit Profile\` -> \`Advanced\`.
`
  },
  'new-question-form': {
    title: 'New Question',
    text: `Enter a **title** and the full question editor will appear in \`draft\` (only visible to you).`,
    placement: 'bottom-start'
  },
  'question-status-editable': {
    title: 'Question Status',
    text: `The question status determines who can see it and a variety of app behaviors. New questions start in \`draft\` - change to \`review\`, \`question generation\`, or \`forecasting\` when you are ready for others to see it.
  \n![Question status](/tour/question-status.png?swift-align=center)
    `,
    more: 'Question-27f0cba1ade58103839de4831e3f130e#27f0cba1ade581fb9cced039d64bb374'
  },
  'forecast-type-editable': {
    title: 'Forecast Type',
    text: `The forecast type can be one of:\n\n${values(questionForecastMd)
      .map(({label, desc}) => `- **${label}**: ${desc}`)
      .join('\n')}
\nThis will impact the way forecasts are entered. You can change this at any time, but existing forecasts will become redundant.
\n![Question type](/tour/question-type.gif?swift-align=center)
    `,
    more: 'Forecasting-27f0cba1ade5814491b7cc4a8f5b0b98#27f0cba1ade5818ab24ac6c7a86317ee'
  },
  'new-question-amount': {
    title: 'Quantity Question Configuration',
    text: `
  \n- **Display range**: The display range is the default zoom for new forecast and the display of the results. Hard cut-off should be set when values outside the range are not-allowed (e.g. less than 0 deaths). It is important to set hard cut-off before forecasts are made.
  \n- **Axis mode**: either \`number\` or \`date\`.
  \n\`Number\` axis mode also has the following options:
  \n- **Number format**: if you want to display the number in a specific format (e.g., currency).
  \n- **Unit**: the unit of the quantity being forecasted (e.g., people, USD). This is important to set so it is unambiguous what is being forecast. 
  `
  },
  'new-question-propose-subq': {
    title: 'Propose Sub-question',
    text: `To add conditional questions or related questions, click the Propose a Sub-question button and enter a title. The details for the subquestion will be suggested based on the title, and you can override them if you want.`,
    more: 'Question-27f0cba1ade58103839de4831e3f130e#27f0cba1ade58184887ec3e5eae545ab'
  },
  'new-question-propose-subq-type': {
    title: 'Sub-question Type',
    text: `The type of subquestion for conditional forecasting.`,
    more: 'Question-27f0cba1ade58103839de4831e3f130e#27f0cba1ade581f7a536fb98882478b7'
  },
  'subq-status-editable': {
    title: 'Sub-question Status',
    text: `Sub-questions start as proposed. This gives an opportunity for forecasters to propose and vote on sub-questions before forecasting.
\nWhen you want to start forecasting on them, change the status to \`approved\` by selecting from the dropdown or clicking the approve button to the right.`
  },
  'subq-voting': {
    title: 'Voting',
    text: `Vote on others' sub-questions to help the question admin decide which ones to forecast on. The proposed sub-questions are displayed with the most votes at the top.`
  },
  'subq-edit-button': {
    title: 'Edit Sub-question',
    text: `Click to edit the sub-question. Editable options will display in-place and will save automatically. Click the tick when you are finished editing.
\n![Sub-question edit](/tour/subq-edit.gif?swift-align=center)`
  },
  'forecast-likelihood': {
    title: 'Likelihood',
    text: `Forecast the percent likelihood of an event happening.`,
    more: 'Forecasting-27f0cba1ade5814491b7cc4a8f5b0b98#27f0cba1ade581b889e9d776eb36d7ba',
    placement: 'bottom',
    video: 'forecast-likelihood-v2.mp4'
  },
  'forecast-amount': {
    title: 'Quantity Forecast',
    text: `Forecast probability over a range of values by entering a 50th percentile value and left and right tail lengths.`,
    more: 'Forecasting-27f0cba1ade5814491b7cc4a8f5b0b98#27f0cba1ade58186a7e9e90408627dfc',
    placement: 'bottom',
    video: 'forecast-quantity.mp4'
  },
  'forecast-draw': {
    title: 'Quantity Drawing',
    text: `Control your forecast by drawing your probability curve over a range of values.`,
    video: 'forecast-draw.mp4',
    more: 'Forecasting-27f0cba1ade5814491b7cc4a8f5b0b98#27f0cba1ade581c5b3c2cf7c32cde78a'
  },
  'forecast-submit': {
    title: 'Submitting a Forecast',
    text: `Submit your forecast by clicking the \`Forecast\` button.
- Enter a note to post with the forecast (optional).
- Click the \`x\` beside draft status to remove any edits since the last submission.
    `,
    video: 'submitting-forecast.mp4'
  },
  'comment-toggle-modal': {
    title: 'Comment/Question View',
    text: `Click to toggle the view between the question and comments. 
- Shows the number of comments, with the status circle showing new comments if there are any.`
  },
  'comment-button-collapsed': {
    title: 'Comment Panel Toggle',
    text: `Click to show/hide the comments panel.`
  },
  comparisons: {
    title: 'Comparisons',
    text: `Forecasters and admins can add comparisons to any question. These can be relevant base rates or external forecasts - anything you want to compare against.`,
    more: 'Question-27f0cba1ade58103839de4831e3f130e#27f0cba1ade5810ab11ce2fe6c9d9640',
    video: 'comparisons.mp4'
  }
} satisfies TourCfg

export type TourId = keyof typeof tourCfg

export const tourTipCfg = (id: TourId) => doSafe(tourCfg[id], t => ({...t, id}))
export const tourTipProps = (id: TourId, enabled: boolean = true) =>
  enabled ? doSafe(tourTipCfg(id), t => ({'data-tip-id': t.id})) : undefined

export const zTourSettings = z.object({
  seen: z.array(zx.enumPassthrough(zx.enumFromObjKeys(tourCfg))).default([]),
  noTips: z.boolean().optional()
})

export type TourSettings = z.infer<typeof zTourSettings>
